import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { useGetConfigurationQuery } from '@/app/api/resourceApi';
import GlobalLoader from '@/components/ui/GlobalLoader';
import MaskIcon from '@/components/ui/MaskIcon';
import {
  useWaiterTablesQuery,
  useListTakeawayQuery,
  useOpenJoinTableMutation,
  useJoinTakeawayMutation,
  useGetSessionDetailQuery,
  useUnlockSessionOrderingMutation,
  waiterApi,
} from '@/features/waiter/waiterApi';
import {
  selectActiveTicket,
  selectWaiterAuth,
  setTicket,
  clearTicket,
  setLockedFrom,
  setUiLocked,
} from '@/features/waiter/waiterSlice';
import { useWaiterAuthGuard } from '@/features/waiter/useWaiterAuthGuard';
import {
  selectWaiterCartLines,
  selectWaiterLastTouched,
  resolveLastLine,
  clearWaiterCartSession,
  forgetWaiterCartSession,
  hydrateWaiterCart,
  addWaiterLine,
  readWaiterCartRecovery,
  dropTypedWaiterCartRecovery,
} from '@/features/waiter/waiterCartSlice';
import { useWaiterCartRecovery } from '@/features/waiter/useWaiterCartRecovery';
import { formatTicketTime } from '@/features/waiter/ticketTime';
import { useIdleLock } from '@/features/waiter/useIdleLock';
import type { TakeOverNavigationState, TicketArrivalState } from '@/features/waiter/useTakeOverRound';
import {
  useAdaptivePolling,
  useObservedPollResponse,
} from '@/features/waiter/useAdaptivePolling';
import TicketHeader from './TicketHeader';
import TicketPlats from './TicketPlats';
import { ticketMenuPeriod } from './ticketMenuPeriod';
import { restaurantTz } from '@/lib/hours';
import TicketCartModal from './TicketCartModal';
import TicketOptionsModal, { type OptionsTarget } from './TicketOptionsModal';
import TicketConfirmRound from './TicketConfirmRound';
import TicketCommandes from './TicketCommandes';
import TicketFooter from './TicketFooter';
import { useScrimDismiss } from './useScrimDismiss';
import type { ShowWaiterToast } from './waiterToast';
import TicketPaidBanner from './TicketPaidBanner';
import TakeawayNameModal from './TakeawayNameModal';

interface Props {
  restaurantUuid: string;
  token: string;
  /** Owned by `WaiterShell` (one level above the nested `<Routes>`) so a
   *  toast that also navigates away — e.g. « Commande introuvable » —
   *  survives the unmount that navigation itself causes here. */
  showToast: ShowWaiterToast;
}

/**
 * Spec §1.7 D1, 06-test-cases.md §1.3/§1.10 — the note screen: re-joins from
 * the self-sufficient URL when no ticket context is in memory (cold URL /
 * reload / a card tap, which never sets one — see F1's `NotesListPage`),
 * owns the header / tabs / leave guard / idle lock / SSE-driven session-detail
 * poll, and hosts the Plats tab. Commandes content is F4's, the Partir /
 * Imprimer / ✔ footer is F5's, the options + cart sheets are F3's — all three
 * get bare placeholder slots with the agreed testids below.
 */
export default function TicketPage({ restaurantUuid, token, showToast }: Props) {
  const { sessionUuid = '' } = useParams<{ sessionUuid: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const activeTicket = useAppSelector(selectActiveTicket);
  const auth = useAppSelector(selectWaiterAuth);
  const isCurrent = activeTicket?.sessionUuid === sessionUuid;

  const [activeTab, setActiveTab] = useState<'plats' | 'commandes'>('plats');
  const [leaveGuardOpen, setLeaveGuardOpen] = useState(false);
  // SC-935 — « Emporter » temporaire sur une table : la table a fini de manger
  // et repart avec un plat. Volontairement un state de composant et rien
  // d'autre — `TicketPage` est démonté dès qu'on quitte le ticket, donc chaque
  // retour sur la table repart en sur place. Ne JAMAIS le persister (redux,
  // sessionStorage, URL) : la règle produit est que la bascule ne survit pas à
  // la sortie de l'écran.
  const [emporterMode, setEmporterMode] = useState(false);
  const [emporterGuardOpen, setEmporterGuardOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [optionsTarget, setOptionsTarget] = useState<OptionsTarget | null>(null);
  const [confirmRoundOpen, setConfirmRoundOpen] = useState(false);
  // SC-1466 — the client's name of an Emporter note: `afterOrder` when it
  // opens by itself because the round just went out under « Client N ».
  const [nameModal, setNameModal] = useState<{ afterOrder: boolean } | null>(null);
  // F5's Partir, when the waiter cart is non-empty, goes through this SAME
  // guard first (W-T-85) rather than a second one — the guard always means
  // "Vider et quitter" for the ← back tap; F5 stashes its own continuation
  // (open `leave-confirm`) so "Vider" resumes into ITS flow instead.
  const afterGuardClearRef = useRef<() => void>(() => {});

  // sessionStorage → redux, per-session (spec §1.7 "panier isolé").
  useEffect(() => {
    if (sessionUuid) dispatch(hydrateWaiterCart(sessionUuid));
  }, [dispatch, sessionUuid]);

  const cartLines = useAppSelector(selectWaiterCartLines(sessionUuid));
  const lastTouched = useAppSelector(selectWaiterLastTouched(sessionUuid));

  // SC-953 — arriving from « Modifier » on a customer's round: its dishes are
  // already in this cart, so the ticket opens on the cart, Plats tab behind
  // it. The state is consumed (replaced) so a reload or Back does not reopen.
  const location = useLocation();
  const openCartOnArrival = (location.state as TakeOverNavigationState | null)?.openWaiterCart === true;
  // DF-0223 — arrivee par la pastille rouge d'une tuile : le ticket s'ouvre sur
  // l'onglet Commandes, feuille de la ronde deployee. Meme consommation de
  // l'etat que ci-dessus, pour qu'un retour ne la rouvre pas.
  const openPendingSheetOnArrival =
    (location.state as TicketArrivalState | null)?.openPendingSheet === true;
  const [pendingSheetRequest, setPendingSheetRequest] = useState(0);

  // Ticket context is per-route, not per-app-lifetime (spec §1.7) — dropped on
  // every unmount; a later remount at the same URL re-joins (harmless,
  // open-or-join), and the cart itself survives independently in
  // sessionStorage.
  useEffect(() => {
    return () => {
      dispatch(clearTicket());
    };
  }, [dispatch]);

  // --- Session detail: the single source of truth for "is this session
  // still open?" (team-lead backend confirmation, F2 follow-up). Runs
  // regardless of `isCurrent` — the re-join effect below needs it BEFORE it
  // may ever call a join endpoint — and polls every 5s only once the ticket
  // is actually the active one (SSE-invalidated meanwhile via task C's
  // `SessionDetail` tag).
  const [isVisible, setIsVisible] = useState(!document.hidden);
  useEffect(() => {
    const handler = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // DF-0177 — le sondage recule quand la reponse ne change pas. Une tablette
  // laissee sur le ticket d'une table ouverte a redemande la MEME session
  // 5 330 fois en 24 h (une fois toutes les ~16 s), et `waiter-mode` pesait
  // 8,8 % de tout le trafic de la plateforme pour deux tablettes. Le flux SSE
  // porte deja les vrais changements ; ce sondage n'est que le filet quand il
  // tombe, et un filet n'a pas besoin de battre a 5 s pendant six heures sur
  // une table ou rien ne se passe.
  const [sessionDetailPollMs, observeSessionDetail] = useAdaptivePolling(
    isCurrent && isVisible,
    5_000,
    60_000,
  );
  const {
    data: sessionDetail,
    error: sessionDetailError,
    fulfilledTimeStamp: sessionDetailStamp,
  } = useGetSessionDetailQuery(
    { sessionUuid, token },
    {
      skip: !token || !sessionUuid,
      pollingInterval: sessionDetailPollMs,
      // DF-0504 — le re-join plus bas n'a le droit de se fier qu'a une reponse
      // demandee depuis l'ouverture de CE ticket (voir `detailFreshSince`).
      refetchOnMountOrArgChange: true,
    },
  );
  useObservedPollResponse(observeSessionDetail, sessionDetail, sessionDetailStamp);

  // Spec 2026-08-28 paid-table-ordering-lock §5.4 — rouvrir la prise de
  // commande d'une table que le convive a soldee. Le sondage 5 s ci-dessus
  // rafraichit la banniere ; pas d'invalidation manuelle a orchestrer.
  const [unlockOrdering, { isLoading: unlocking }] = useUnlockSessionOrderingMutation();
  const handleReopenOrdering = async () => {
    try {
      await unlockOrdering({ sessionUuid, token }).unwrap();
    } catch {
      showToast(t('waiter.ticket.paid.reopenFailed'));
    }
  };

  // --- "Gone" detection — SINGLE source of truth, deliberately shared by
  // both the cold-URL join path and the already-viewing path (W-T-03/04/06,
  // F2 follow-up). A 404 means unknown/cross-tenant; a 200 with
  // `status !== 'open'` means closed/finished/cancelled/moved elsewhere.
  // Either way this is terminal: toast, drop the ticket context, leave.
  //
  // This MUST be the only place that reacts to a "gone" session, and its
  // guard ref MUST be shared with the join effect below. Splitting "gone
  // while joining" from "gone while viewing" into two separately-guarded
  // effects looked simpler at first but has a real bug: this effect's own
  // `dispatch(clearTicket())` flips `isCurrent` to false on the very next
  // render, which un-gates a differently-guarded join effect and lets it
  // react to the SAME stale error a second time — clobbering the toast this
  // effect just set with its own (wrong, "introuvable") message. One ref,
  // set synchronously before either dispatch, closes that hole.
  const goneHandledFor = useRef<string | null>(null);
  useEffect(() => {
    if (goneHandledFor.current === sessionUuid) return;

    if (sessionDetailError) {
      const status = (sessionDetailError as { status?: number | string } | undefined)?.status;
      // 401/403 (dead waiter JWT) is handled by the dedicated auth effect
      // below — nothing terminal to do here for those.
      if (status === 404) {
        goneHandledFor.current = sessionUuid;
        showToast(t('waiter.ticket.errors.sessionNotFound'));
        dispatch(clearTicket());
        navigate(`/${restaurantUuid}/serveur`);
      }
      return;
    }

    if (sessionDetail && sessionDetail.session.status !== 'open') {
      goneHandledFor.current = sessionUuid;
      showToast(t('waiter.ticket.closedElsewhere'));
      dispatch(forgetWaiterCartSession(sessionUuid));
      dispatch(clearTicket());
      navigate(`/${restaurantUuid}/serveur`);
    }
  }, [sessionDetail, sessionDetailError, sessionUuid, dispatch, navigate, restaurantUuid, showToast, t]);

  // SC-976 review P2#9 (E-34, cross-device) — an Ajouter/Séparer done from
  // ANOTHER tablet, or from the admin/owner Salle, invalidates this
  // session's `SessionDetail` tag (SSE `table_session_updated`), so the poll
  // above sees the new `tableLabel`/`isMerged` before anything on THIS
  // device ever touched them. Same "patch the open ticket" pattern as
  // SC-958's covers save (`NotesListPage.tsx`'s `saveCovers`), driven here
  // from the session detail instead of a local mutation's own response.
  // Takeaway notes carry no `tableLabel` (`undefined`) — the guard leaves
  // them untouched.
  useEffect(() => {
    if (!isCurrent || !activeTicket || activeTicket.kind !== 'dine_in' || !sessionDetail) return;
    const nextLabel = sessionDetail.session.tableLabel;
    if (nextLabel === undefined) return;
    const nextMerged = sessionDetail.session.isMerged === true;
    if (nextLabel === activeTicket.label && nextMerged === (activeTicket.isMerged === true)) return;
    dispatch(setTicket({ ...activeTicket, label: nextLabel, isMerged: nextMerged }));
  }, [isCurrent, activeTicket, sessionDetail, dispatch]);

  // SC-1466 — same « patch the open ticket » pattern for an Emporter note's
  // name: given from ANOTHER device, it reaches this header via the poll.
  useEffect(() => {
    if (!isCurrent || !activeTicket || activeTicket.kind !== 'takeaway' || !sessionDetail) return;
    const nextLabel = sessionDetail.session.label;
    if (!nextLabel || nextLabel === activeTicket.label) return;
    dispatch(setTicket({ ...activeTicket, label: nextLabel }));
  }, [isCurrent, activeTicket, sessionDetail, dispatch]);
  const labelProvisional = sessionDetail?.session.labelProvisional === true;

  const handleRenamed = (label: string) => {
    setNameModal(null);
    // The poll would bring the name back within seconds; patching the cached
    // detail now keeps the sync effect above from putting the old one back.
    dispatch(
      waiterApi.util.updateQueryData('getSessionDetail', { sessionUuid, token }, (draft) => {
        draft.session.label = label;
        draft.session.labelProvisional = false;
      }),
    );
    if (activeTicket) dispatch(setTicket({ ...activeTicket, label }));
  };

  // --- Re-join (D1 §1.7, W-T-03) -------------------------------------------
  // No token in memory for THIS session ⇒ the URL alone must recover it. The
  // session detail's `status` gates whether a join is even attempted:
  // `tables/<t>/session/` and `takeaway/<uuid>/session/` are open-OR-join —
  // calling either for a CLOSED session's uuid does not safely no-op, it
  // OPENS A NEW session on that table (a real Salle state change). Only once
  // `status === 'open'` is confirmed (and the "gone" effect above has NOT
  // already claimed this session) do we look it up in the tables/takeaway
  // lists (kind is only known from there) to mint a fresh guest token via
  // the matching join endpoint.
  const { data: tables } = useWaiterTablesQuery({ restaurantUuid, token }, { skip: !token || isCurrent });
  const { data: takeawayNotes } = useListTakeawayQuery(
    { restaurantUuid, token },
    { skip: !token || isCurrent },
  );
  const [openJoinTable] = useOpenJoinTableMutation();
  const [joinTakeaway] = useJoinTakeawayMutation();
  const joinAttemptedFor = useRef<string | null>(null);
  // DF-0504 — RTK Query rend d'abord la fiche gardee en cache (60 s) : celle
  // d'avant que le pont de caisse ferme la session. Rouvert dans cette
  // minute, le ticket la lisait « open » et rejoignait — et
  // `tables/<t>/session/` ouvre une session NEUVE quand la sienne est
  // fermee : table fantome, vide, en « Sur place ». Le re-join attend donc
  // une reponse arrivee apres l'ouverture du ticket.
  const detailFreshSince = useMemo(() => ({ sessionUuid, at: Date.now() }), [sessionUuid]);

  useEffect(() => {
    if (isCurrent) return;
    if (!token || !sessionUuid) return;
    if (goneHandledFor.current === sessionUuid) return;
    if (joinAttemptedFor.current === sessionUuid) return;
    if (sessionDetailStamp === undefined || sessionDetailStamp < detailFreshSince.at) return;
    if (!sessionDetail || sessionDetail.session.status !== 'open') return;
    if (tables === undefined || takeawayNotes === undefined) return;
    joinAttemptedFor.current = sessionUuid;

    // SC-976 review P1#3 — a merged session's member row carries the SAME
    // session uuid as its anchor; a bare `.find` can land on the member,
    // which then re-joins via `tables/<member-uuid>/session/` (still
    // correct server-side — open-or-join resolves to the existing session)
    // but falls back to the MEMBER's own number for `label` if the server
    // ever omits `tableLabel`. Prefer the anchor row explicitly.
    const table =
      tables.find((tb) => tb.session?.uuid === sessionUuid && tb.role !== 'member') ??
      tables.find((tb) => tb.session?.uuid === sessionUuid);
    const note = !table ? takeawayNotes.find((n) => n.uuid === sessionUuid) : undefined;

    const bail = () => {
      showToast(t('waiter.ticket.errors.sessionNotFound'));
      navigate(`/${restaurantUuid}/serveur`);
    };

    (async () => {
      try {
        if (table?.session) {
          const result = await openJoinTable({ tableUuid: table.uuid, token }).unwrap();
          dispatch(
            setTicket({
              sessionUuid,
              kind: 'dine_in',
              label: result.session.tableLabel ?? String(table.number),
              covers: result.session.covers,
              openedAt: result.session.openedAt,
              guestToken: result.guestToken,
              isMerged: result.session.isMerged,
            }),
          );
        } else if (note) {
          const result = await joinTakeaway({ noteUuid: sessionUuid, token }).unwrap();
          dispatch(
            setTicket({
              sessionUuid,
              kind: 'takeaway',
              label: result.session.label,
              covers: result.session.covers,
              openedAt: result.session.openedAt,
              guestToken: result.guestToken,
            }),
          );
        } else {
          bail();
        }
      } catch {
        bail();
      }
    })();
  }, [
    isCurrent,
    sessionDetail,
    sessionDetailStamp,
    detailFreshSince,
    tables,
    takeawayNotes,
    sessionUuid,
    token,
    restaurantUuid,
    dispatch,
    navigate,
    openJoinTable,
    joinTakeaway,
    showToast,
    t,
  ]);

  // --- Dead waiter JWT on a ticket call (W-T-86) --------------------------
  // WAITER_REVOKED / WAITER_MODE_DISABLED: the waiter JWT (not the guest
  // token) is dead. Cart stays (sessionStorage); WaiterShell falls back to the
  // PinPad once auth is cleared. DF-0193 — le garde vit maintenant dans
  // `useWaiterAuthGuard`, partagé avec les trois autres surfaces.
  useWaiterAuthGuard(restaurantUuid, [sessionDetailError]);

  // --- Idle lock (spec §1.7 / W-T-89/90) ---------------------------------
  const lockAndReturnToList = useCallback(() => {
    dispatch(setUiLocked(true));
    dispatch(setLockedFrom({ kind: 'ticket', sessionUuid }));
    navigate(`/${restaurantUuid}/serveur`);
  }, [dispatch, sessionUuid, restaurantUuid, navigate]);

  useIdleLock(auth?.idleLockSeconds, !!auth && !auth.uiLocked, lockAndReturnToList);

  // --- Leave guard (W-T-87/88, and W-T-85's Partir-with-unsent-cart) ------
  const goToList = useCallback(() => navigate(`/${restaurantUuid}/serveur`), [navigate, restaurantUuid]);
  const handleBack = () => {
    if (cartLines.length > 0) {
      afterGuardClearRef.current = goToList;
      setLeaveGuardOpen(true);
    } else {
      goToList();
    }
  };
  // F5's Partir tap (`TicketFooter`'s `onRequestCartGuard`): same guard, a
  // different continuation once the waiter picks « Vider et quitter ».
  const requestLeaveGuard = useCallback((onCleared: () => void) => {
    afterGuardClearRef.current = onCleared;
    setLeaveGuardOpen(true);
  }, []);
  // Appui sur le fond = « Rester » : rien n'est vidé, la suite mise de côté
  // ne part pas.
  const onLeaveGuardScrim = useScrimDismiss(leaveGuardOpen, () => setLeaveGuardOpen(false));
  const clearAndLeave = () => {
    dispatch(clearWaiterCartSession(sessionUuid));
    setLeaveGuardOpen(false);
    afterGuardClearRef.current();
  };

  // --- Archive actions (F5 finish/close/cancel) — every path ends here.
  const handleFinished = useCallback(() => {
    dispatch(forgetWaiterCartSession(sessionUuid));
    goToList();
  }, [dispatch, sessionUuid, goToList]);

  // --- Menu period (no scheduling on the waiter surface — always "now" for
  // a table; the emporter note reads the same window as the online emporter
  // carte, see `ticketMenuPeriod`) ---------------------------------------
  const { data: identity } = useGetRestaurantIdentityQuery(restaurantUuid, { skip: !restaurantUuid });
  const { data: restaurantConfig } = useGetConfigurationQuery(restaurantUuid, { skip: !restaurantUuid });
  // SC-935 — le mode de VENTE de la tournée en cours, distinct du genre du
  // ticket (`activeTicket.kind`, qui reste ce qu'il est : une table est une
  // table). Une table basculée en emporter se comporte de bout en bout comme
  // une note Emporter — carte filtrée sur `available_emporter`, prix emporter,
  // même fenêtre de service que la carte emporter en ligne, tournée envoyée en
  // `take_out` — mais reste rattachée à sa session, qui garde l'addition. Une
  // note Emporter, elle, n'a rien à basculer.
  const orderKind: 'dine_in' | 'takeaway' =
    activeTicket?.kind === 'dine_in' && emporterMode ? 'takeaway' : (activeTicket?.kind ?? 'dine_in');

  const period = ticketMenuPeriod(
    orderKind,
    identity?.hours,
    new Date(),
    identity?.closures ?? [],
    restaurantConfig?.prepTimeEmporterMin ?? 0,
    restaurantTz(identity ?? {}),
  );

  // SC-935 (2026-09-08) — la bascule n'existe que si le restaurateur l'a
  // ouverte (`table_emporter_round_enabled`, admin → Commande en ligne → Sur
  // place). Fermée par défaut : beaucoup de maisons ne veulent pas mélanger
  // deux modes de vente sur une même addition. sale_api tient le même garde
  // (403 `TABLE_EMPORTER_ROUND_DISABLED`) — celui-ci n'est que l'entrée.
  const emporterToggleAvailable = restaurantConfig?.tableEmporterRoundEnabled === true;

  // Le buffet est un forfait de salle : une tournée vendue à emporter se
  // commande à la carte, comme sur le site.
  const isBuffetDineIn = orderKind === 'dine_in' && sessionDetail?.session.mode === 'buffet';

  // Une tournée ne peut pas mélanger sur place et emporter : c'est UN type de
  // commande, un jeu de prix et une TVA. Le panier en cours est donc vidé —
  // après confirmation — quand le serveur change d'avis en cours de saisie.
  //
  // 2026-09-08 — et ALLUMER la bascule demande toujours confirmation, panier
  // vide compris : le bouton est à deux doigts de l'heure, en haut d'un écran
  // qu'on manipule debout, et un appui involontaire change silencieusement les
  // prix et la TVA de la tournée qu'on est en train de saisir. L'éteindre reste
  // immédiat quand il n'y a rien à jeter — revenir sur place est le mode par
  // défaut, jamais une surprise.
  const requestEmporterToggle = () => {
    if (!emporterMode || cartLines.length > 0) {
      setEmporterGuardOpen(true);
      return;
    }
    setEmporterMode(false);
  };
  const confirmEmporterToggle = () => {
    dispatch(clearWaiterCartSession(sessionUuid));
    setEmporterGuardOpen(false);
    setEmporterMode((on) => !on);
  };

  useEffect(() => {
    if (!isCurrent || !openCartOnArrival) return;
    setActiveTab('plats');
    setCartModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [isCurrent, openCartOnArrival, navigate, location.pathname]);

  useEffect(() => {
    if (!isCurrent || !openPendingSheetOnArrival) return;
    setActiveTab('commandes');
    setPendingSheetRequest((n) => n + 1);
    navigate(location.pathname, { replace: true, state: null });
  }, [isCurrent, openPendingSheetOnArrival, navigate, location.pathname]);

  // --- DF-0224 / DF-0383 : les plats qu'un onglet perdu a laissés -----------
  // Le panier meurt avec l'onglet. Si celui-ci est fermé avant l'envoi, la
  // copie de secours (localStorage, même appareil) est la seule chose qui
  // connaisse encore ces plats — ceux d'une ronde reprise à un client
  // (« Modifier » l'a sortie de la file de tous les appareils) comme ceux que
  // le serveur a saisis. On ne les rejoue JAMAIS en silence — deux onglets
  // renverraient la même tournée deux fois : on les propose, le serveur décide.
  // « Supprimer » ne touche que le stockage, pas le store : les lignes
  // écartées sont retenues ici, la proposition part sans attendre un rendu.
  const [droppedLineIds, setDroppedLineIds] = useState<string[]>([]);
  const [dropGuardOpen, setDropGuardOpen] = useState(false);
  const recovery = useWaiterCartRecovery(sessionUuid);
  const recoverableLines = useMemo(() => {
    const hidden = new Set([...cartLines.map((line) => line.lineId), ...droppedLineIds]);
    return recovery.lines.filter((line) => !hidden.has(line.lineId));
  }, [recovery, cartLines, droppedLineIds]);
  const unitsOf = (lines: typeof recoverableLines) => lines.reduce((sum, line) => sum + line.qty, 0);
  // Deux natures de plats, deux phrases : ceux que le serveur a saisis portent
  // l'heure de la saisie et peuvent être supprimés ; ceux repris à un client
  // gardent la phrase de DF-0224 et ne se suppriment pas — personne d'autre ne
  // les a.
  const recoverableTyped = recoverableLines.filter((line) => !line.takenOver);
  const typedQty = unitsOf(recoverableTyped);
  const takenOverQty = unitsOf(recoverableLines) - typedQty;
  const recoveryTime =
    typedQty > 0 && recovery.savedAt !== null ? formatTicketTime(new Date(recovery.savedAt).toISOString()) : '';
  const restoreRecoverableLines = () => {
    // Relu à l'appui, et c'est la ligne relue qui entre dans le panier : depuis
    // que ce ticket les affiche, un autre onglet a pu les reprendre — et les
    // envoyer — ou en changer la quantité.
    const shown = new Set(recoverableLines.map((line) => line.lineId));
    for (const line of readWaiterCartRecovery(sessionUuid)) {
      if (shown.has(line.lineId)) dispatch(addWaiterLine({ sessionUuid, line }));
    }
    setCartModalOpen(true);
  };
  const dropTypedRecovery = () => {
    const named = recoverableTyped.map((line) => line.lineId);
    dropTypedWaiterCartRecovery(sessionUuid, named);
    setDroppedLineIds(named);
    setDropGuardOpen(false);
    showToast(
      t(typedQty === 1 ? 'waiter.ticket.unsentRecovery.droppedOne' : 'waiter.ticket.unsentRecovery.droppedOther', {
        n: typedQty,
      }),
    );
  };
  const onDropGuardScrim = useScrimDismiss(dropGuardOpen, () => setDropGuardOpen(false));
  // Plus rien à supprimer (un autre onglet vient de les reprendre) : la fenêtre
  // se referme, elle ne doit pas se rouvrir seule sur les plats suivants.
  useEffect(() => {
    if (dropGuardOpen && typedQty === 0) setDropGuardOpen(false);
  }, [dropGuardOpen, typedQty]);
  // Le garde de sortie ne dit pas la même chose selon ce qu'on jette : un plat
  // saisi par le serveur se retape, un plat repris au client est perdu.
  const cartHasTakenOver = cartLines.some((line) => line.takenOver);

  // The row's blue button = « note ou options du dernier plat ajouté » of
  // that product (old-app `cart.findLast`, SC-842) — the line the last
  // `+` / Valider landed on, not merely the product's bottom-most line.
  const findLastLine = (itemId: string) => resolveLastLine(cartLines, lastTouched, itemId);

  return (
    <div className="waiter-root bg-ash" data-testid="ticket-page">
      {isCurrent && activeTicket ? (
        <>
          <TicketHeader
            kind={activeTicket.kind}
            label={activeTicket.label}
            covers={activeTicket.covers}
            openedAt={activeTicket.openedAt}
            onBack={handleBack}
            emporter={emporterMode}
            merged={activeTicket.isMerged}
            onToggleEmporter={emporterToggleAvailable ? requestEmporterToggle : undefined}
            onRename={activeTicket.kind === 'takeaway' ? () => setNameModal({ afterOrder: false }) : undefined}
            labelProvisional={labelProvisional}
          />
          <TicketPaidBanner
            paidTotal={sessionDetail?.session.paidTotal}
            dueTotal={sessionDetail?.session.dueTotal}
            orderingLocked={sessionDetail?.session.orderingLocked === true}
            onReopen={handleReopenOrdering}
            busy={unlocking}
          />
          {/* DF-0224 / DF-0383 — la seule trace, sur cet appareil, de plats
              jamais envoyés : une ronde reprise à un client, ou ce que le
              serveur avait saisi dans un onglet perdu. Le panier rouge est
              celui de la pastille que la salle montre sur la table. */}
          {recoverableLines.length > 0 && (
            <div
              data-testid="taken-over-recovery"
              className="shrink-0 bg-paper border-b-2 border-hairline px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <p id="waiter-recovery-message" className="font-sans text-[15px] flex-1 min-w-[12rem]">
                <span className="me-1.5 align-[-2px]" style={{ color: 'var(--w-danger)' }}>
                  <MaskIcon src="/img/icon-cart.svg" className="w-4 h-4" />
                </span>
                {typedQty > 0 &&
                  t(
                    typedQty === 1
                      ? 'waiter.ticket.unsentRecovery.messageOne'
                      : 'waiter.ticket.unsentRecovery.messageOther',
                    { n: typedQty, time: recoveryTime },
                  )}
                {typedQty > 0 && takenOverQty > 0 && ' '}
                {takenOverQty > 0 && t('waiter.ticket.takenOverRecovery.message', { count: takenOverQty })}
              </p>
              <div className="ms-auto flex items-center gap-3">
                {typedQty > 0 && (
                  <button
                    type="button"
                    data-testid="unsent-recovery-drop"
                    aria-describedby="waiter-recovery-message"
                    onClick={() => setDropGuardOpen(true)}
                    className="min-h-[44px] px-2 font-sans text-[16px] underline"
                  >
                    {t('waiter.ticket.unsentRecovery.drop')}
                  </button>
                )}
                <button
                  type="button"
                  data-testid="taken-over-recovery-restore"
                  aria-describedby="waiter-recovery-message"
                  onClick={restoreRecoverableLines}
                  className="min-h-[44px] border-2 border-ink rounded-btn-sm px-3 py-1.5 font-sans text-[16px] font-bold"
                >
                  {t('waiter.ticket.takenOverRecovery.restore')}
                </button>
              </div>
            </div>
          )}
          {/* The height lives on the BUTTONS, not on the row: with it on the
              row (`h-11`), the row's own 2px border plus the buttons' UA
              metrics left each tab at 39px — under the 44px floor §0 asks
              for, measured live on every phone width. */}
          <div role="tablist" className="grid grid-cols-2 shrink-0 bg-paper border-b-2 border-hairline">
            <button
              type="button"
              data-testid="ticket-tab-plats"
              role="tab"
              aria-selected={activeTab === 'plats'}
              onClick={() => setActiveTab('plats')}
              className={`min-h-[48px] font-sans text-lg font-semibold ${
                activeTab === 'plats' ? 'font-bold text-ink border-b-[3px] border-ink bg-ash' : 'text-ink-mute'
              }`}
            >
              {t('waiter.ticket.tabs.plats')}
            </button>
            <button
              type="button"
              data-testid="ticket-tab-commandes"
              role="tab"
              aria-selected={activeTab === 'commandes'}
              onClick={() => setActiveTab('commandes')}
              className={`min-h-[48px] font-sans text-lg font-semibold ${
                activeTab === 'commandes' ? 'font-bold text-ink border-b-[3px] border-ink bg-ash' : 'text-ink-mute'
              }`}
            >
              {t('waiter.ticket.tabs.commandes')}
            </button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            <TicketPlats
              hidden={activeTab !== 'plats'}
              restaurantUuid={restaurantUuid}
              sessionUuid={sessionUuid}
              kind={orderKind}
              period={period}
              isBuffetDineIn={!!isBuffetDineIn}
              buffetRoundItemBudget={sessionDetail?.session.buffetRoundItemBudget}
              onOpenOptionsForNewLine={(itemId) => setOptionsTarget({ mode: 'new', itemId })}
              onEditLastLine={(itemId) => {
                const last = findLastLine(itemId);
                // `lastPlate`: on a « × 3 » line the note/options go to ONE
                // plate (split), the other two stay as they were.
                if (last) setOptionsTarget({ mode: 'edit', lineId: last.lineId, scope: 'lastPlate' });
              }}
              onOpenCart={() => setCartModalOpen(true)}
              onValidate={() => setConfirmRoundOpen(true)}
              showToast={showToast}
            />
            {/* Kept mounted alongside Plats so neither tab loses its scroll
                position (spec §2, "both tabs stay mounted"). */}
            <div
              hidden={activeTab !== 'commandes'}
              className={`flex-1 min-h-0 flex-col ${activeTab === 'commandes' ? 'flex' : 'hidden'}`}
            >
              <TicketCommandes
                hidden={false}
                sessionUuid={sessionUuid}
                restaurantUuid={restaurantUuid}
                token={token}
                label={activeTicket.label}
                showToast={showToast}
                openPendingSheetRequest={pendingSheetRequest}
              />
              <TicketFooter
                sessionUuid={sessionUuid}
                kind={activeTicket.kind}
                label={activeTicket.label}
                restaurantUuid={restaurantUuid}
                token={token}
                hasUnsentCart={cartLines.length > 0}
                onRequestCartGuard={requestLeaveGuard}
                onFinished={handleFinished}
                showToast={showToast}
                onShowCommandes={() => setActiveTab('commandes')}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <GlobalLoader />
        </div>
      )}

      {leaveGuardOpen && (
        <div
          role="alertdialog"
          aria-modal="true"
          data-testid="leave-guard"
          className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
          onClick={onLeaveGuardScrim}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4">
            <p className="font-sans text-lg font-bold">{t('waiter.ticket.leaveGuard.title')}</p>
            <p className="font-sans text-[18px]">
              {(() => {
                const n = cartLines.reduce((s, l) => s + l.qty, 0);
                return n === 1
                  ? t('waiter.ticket.leaveGuard.messageOne', { n })
                  : t('waiter.ticket.leaveGuard.messageOther', { n });
              })()}
            </p>
            {/* DF-0224 — vider un panier qui contient des plats REPRIS au client
                les supprime pour de bon : la ronde a déjà quitté la file de tous
                les appareils, personne d'autre ne les a. */}
            {cartHasTakenOver && (
              <p className="font-sans text-[16px] font-bold" data-testid="leave-guard-taken-over">
                {t('waiter.ticket.leaveGuard.takenOverWarning')}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                data-testid="leave-guard-stay"
                onClick={() => setLeaveGuardOpen(false)}
                className="min-h-[44px] px-2 font-sans text-[18px] underline"
              >
                {t('waiter.ticket.leaveGuard.stay')}
              </button>
              <button
                type="button"
                data-testid="leave-guard-clear"
                onClick={clearAndLeave}
                className="min-h-[44px] text-white border-2 border-ink rounded-btn-sm px-3 py-1.5 font-sans text-[18px] font-bold"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.leaveGuard.discard')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DF-0383 — « Supprimer » jette des plats que le lecteur n'a peut-être
          pas saisis lui-même (appareil partagé) : il les voit nommés avant de
          décider. Même forme que le garde de sortie, rouge = on jette. */}
      {dropGuardOpen && recoverableTyped.length > 0 && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="unsent-drop-guard-title"
          data-testid="unsent-drop-guard"
          className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
          onClick={onDropGuardScrim}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4">
            <p id="unsent-drop-guard-title" className="font-sans text-lg font-bold">
              {typedQty === 1
                ? t('waiter.ticket.unsentRecovery.dropTitleOne')
                : t('waiter.ticket.unsentRecovery.dropTitleOther', { n: typedQty })}
            </p>
            <p className="font-sans text-[16px]">{t('waiter.ticket.unsentRecovery.dropBody', { time: recoveryTime })}</p>
            <ul data-testid="unsent-drop-guard-lines" className="max-h-[40vh] overflow-y-auto flex flex-col gap-1">
              {recoverableTyped.map((line) => (
                <li key={line.lineId} className="font-sans text-[18px] flex gap-2">
                  <span className="font-mono font-bold shrink-0">
                    <bdi>{line.qty}</bdi> ×
                  </span>
                  <span className="min-w-0 break-words">{line.snapshot.name}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                data-testid="unsent-drop-guard-cancel"
                onClick={() => setDropGuardOpen(false)}
                className="min-h-[44px] px-2 font-sans text-[18px] underline"
              >
                {t('waiter.ticket.unsentRecovery.cancel')}
              </button>
              <button
                type="button"
                data-testid="unsent-drop-guard-confirm"
                onClick={dropTypedRecovery}
                className="min-h-[44px] text-white border-2 border-ink rounded-btn-sm px-3 py-1.5 font-sans text-[18px] font-bold"
                style={{ backgroundColor: 'var(--w-danger)' }}
              >
                {t('waiter.ticket.unsentRecovery.drop')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SC-935 — changer de mode en cours de saisie : une tournée ne peut
          pas mélanger sur place et emporter, donc le panier part avec le
          mode. Même forme que le garde de sortie ci-dessus. Depuis le
          2026-09-08 il s'ouvre AUSSI pour allumer la bascule sur un panier
          vide : il n'y a alors rien à jeter, seulement à confirmer. */}
      {emporterGuardOpen && (
        <div
          role="alertdialog"
          aria-modal="true"
          data-testid="emporter-guard"
          className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--w-scrim)' }}
        >
          <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4">
            <p className="font-sans text-lg font-bold">
              {emporterMode
                ? t('waiter.ticket.emporterGuard.titleOff')
                : t('waiter.ticket.emporterGuard.titleOn')}
            </p>
            <p className="font-sans text-[18px]">
              {(() => {
                const n = cartLines.reduce((sum, line) => sum + line.qty, 0);
                if (n === 0) return t('waiter.ticket.emporterGuard.messageEmpty');
                return n === 1
                  ? t('waiter.ticket.emporterGuard.messageOne', { n })
                  : t('waiter.ticket.emporterGuard.messageOther', { n });
              })()}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                data-testid="emporter-guard-cancel"
                onClick={() => setEmporterGuardOpen(false)}
                className="min-h-[44px] px-2 font-sans text-[18px] underline"
              >
                {t('waiter.ticket.emporterGuard.cancel')}
              </button>
              {/* Rouge = « on jette quelque chose ». Sur un panier vide il
                  n'y a rien à jeter : le bouton reprend le violet de la
                  bascule elle-même, et son mot ne parle plus de vider. */}
              <button
                type="button"
                data-testid="emporter-guard-confirm"
                onClick={confirmEmporterToggle}
                className="min-h-[44px] text-white border-2 border-ink rounded-btn-sm px-3 py-1.5 font-sans text-[18px] font-bold"
                style={{
                  backgroundColor: cartLines.length > 0 ? 'var(--w-danger)' : 'var(--w-online)',
                }}
              >
                {cartLines.length > 0
                  ? t('waiter.ticket.emporterGuard.confirm')
                  : t('waiter.ticket.emporterGuard.confirmEmpty')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart sheet (06-test-cases.md W-T-31..36). Its own overlay-tap
          handler closes the OPTIONS modal first if that one is stacked on
          top (spec §2.3 "old behaviour kept") — only once options is gone
          does a further tap actually close the cart. */}
      {cartModalOpen && activeTicket && (
        <TicketCartModal
          sessionUuid={sessionUuid}
          restaurantUuid={restaurantUuid}
          kind={orderKind}
          onClose={() => {
            if (optionsTarget) {
              setOptionsTarget(null);
            } else {
              setCartModalOpen(false);
            }
          }}
          onEditLine={(lineId) => setOptionsTarget({ mode: 'edit', lineId, scope: 'line' })}
        />
      )}

      {/* Options sheet — new-line (row `+` on a required group, no line
          added yet) or edit-line (cart line name = the whole line, row blue
          button = the last plate only), per `optionsTarget`. Always the
          top-most modal: cancelling it never touches the cart sheet
          underneath. */}
      {optionsTarget && activeTicket && (
        <TicketOptionsModal
          sessionUuid={sessionUuid}
          restaurantUuid={restaurantUuid}
          kind={orderKind}
          isBuffetDineIn={!!isBuffetDineIn}
          target={optionsTarget}
          onCancel={() => setOptionsTarget(null)}
        />
      )}

      {/* « Voulez-vous confirmer le panier ? » (F4, W-T-49..58) — on 201,
          switches to Commandes so the just-sent round is visible at once. */}
      {confirmRoundOpen && activeTicket && (
        <TicketConfirmRound
          sessionUuid={sessionUuid}
          restaurantUuid={restaurantUuid}
          kind={orderKind}
          ticketKind={activeTicket.kind}
          ticketLabel={activeTicket.label}
          sessionMode={sessionDetail?.session.mode}
          guestToken={activeTicket.guestToken}
          token={token}
          onClose={() => setConfirmRoundOpen(false)}
          onConfirmed={() => {
            setConfirmRoundOpen(false);
            setActiveTab('commandes');
            // SC-1466 — « Plus tard » : the order is out, now the name.
            if (activeTicket.kind === 'takeaway' && labelProvisional) setNameModal({ afterOrder: true });
            // 2026-09-08 — la bascule vaut pour UNE tournée, pas pour la
            // table : dès que la tournée est partie, le ticket revient sur
            // place. Sans ça la tournée suivante repartait à emporter parce
            // que personne n'avait pensé à rappuyer sur le bouton, et le
            // serveur ne le voyait qu'aux prix de l'addition.
            setEmporterMode(false);
          }}
          onEditLine={(lineId) => {
            // DF-0106 — the options sheet (z-1150) sits UNDER the confirmation
            // (z-1180): close the dialog first; the waiter sends again once
            // the line is complete.
            setConfirmRoundOpen(false);
            setOptionsTarget({ mode: 'edit', lineId, scope: 'line' });
          }}
          showToast={showToast}
        />
      )}

      {isCurrent && activeTicket?.kind === 'takeaway' && (
        <TakeawayNameModal
          open={nameModal !== null}
          noteUuid={sessionUuid}
          token={token}
          currentLabel={activeTicket.label}
          provisional={labelProvisional}
          afterOrder={nameModal?.afterOrder === true}
          onClose={() => setNameModal(null)}
          onRenamed={handleRenamed}
        />
      )}
    </div>
  );
}
